import React, { useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import PlusIcon from "../../Images/PlusIcon.svg";
import CalendarIcon from "../../Images/calendar.svg";
import ClockIcon from "../../Images/clock.svg";
import LocationIcon from "../../Images/Location.svg";

const ProgramSchedule = () => {
  // GET userData FROM OUTLET CONTEXT
  const { userData } = useOutletContext();
  const navigate = useNavigate();

  // HOOKS FIRST
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  // SAFETY LOADER
  if (!userData || !userData.dashboard) {
    return (
      <div className="p-10 text-center text-gray-400 text-lg">
        Loading program schedule...
      </div>
    );
  }

  const dashboard = userData.dashboard;

  const handleclick = () => {
    navigate("/program_schdule/create_schdule");
  };

  const monthsData = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ].map((name, index) => ({ name, index }));

  const generateDatesForMonth = () => {
    const year = selectedDate.getFullYear();
    const daysInMonth = new Date(year, selectedMonth + 1, 0).getDate();
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return Array.from({ length: daysInMonth }, (_, i) => {
      const date = new Date(year, selectedMonth, i + 1);
      return {
        date: i + 1,
        day: dayNames[date.getDay()],
        active:
          selectedMonth === selectedDate.getMonth() &&
          i + 1 === selectedDate.getDate(),
      };
    });
  };

  const dates = generateDatesForMonth();

  const handleMonthChange = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setSelectedDate(new Date(selectedDate.getFullYear(), monthIndex, 1));
  };

  const timeSlots = [
    "08.00","08.30","09.00","09.30","10.00",
    "10.30","11.00","11.30","12.00","12.30",
    "13.00","13.30","14.00","14.30","15.00",
    "15.30","16.00","16.30","17.00","17.30",
    "18.00","18.30","19.00","19.30","20.00",
  ];

  const formatSelectedDate = () => {
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    const day = dayNames[selectedDate.getDay()];
    const month = monthNames[selectedDate.getMonth()];
    const date = String(selectedDate.getDate()).padStart(2, "0");

    return `${day}, ${month} ${date} ${selectedDate.getFullYear()}`;
  };

  // 🔹 Helper: Combine date string and time string into DateTime
  const createDateTime = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    const [hours, minutes] = timeStr.split(':').map(Number);
    date.setHours(hours, minutes, 0, 0);
    return date;
  };

  // 🔹 Calculate event position based on start_time and end_time
  const calculateEventPosition = (schedule) => {
    // Use start_time and end_time from the schedule
    const startDateTime = createDateTime(schedule.start_date, schedule.start_time);
    const endDateTime = createDateTime(schedule.start_date, schedule.end_time);
    
    const startHour = startDateTime.getHours();
    const startMinute = startDateTime.getMinutes();
    const endHour = endDateTime.getHours();
    const endMinute = endDateTime.getMinutes();
    
    // Calculate start position (8:00 AM = 0px, each 30min = 40px)
    const startTimeInMinutes = (startHour * 60) + startMinute;
    const baseTimeInMinutes = 8 * 60; // 8:00 AM
    const minutesFromStart = startTimeInMinutes - baseTimeInMinutes;
    const topPosition = (minutesFromStart / 30) * 40;
    
    // Calculate duration
    const durationInMinutes = ((endHour * 60) + endMinute) - startTimeInMinutes;
    const height = (durationInMinutes / 30) * 40;
    
    return {
      top: Math.max(topPosition, 0),
      height: Math.max(height, 120)
    };
  };

  // 🔹 Check if schedule occurs on selected date
  const isScheduleOnDate = (schedule, checkDate) => {
    const startDate = new Date(schedule.start_date);
    const endDate = new Date(schedule.end_date);
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    const selectedDateStart = new Date(checkDate);
    selectedDateStart.setHours(0, 0, 0, 0);
    const selectedDateEnd = new Date(checkDate);
    selectedDateEnd.setHours(23, 59, 59, 999);
    
    return startDate <= selectedDateEnd && endDate >= selectedDateStart;
  };

  // 🔹 Filter schedules for selected date
  const scheduleData = (dashboard.scheduled_programs_list || [])
    .filter((schedule) => isScheduleOnDate(schedule, selectedDate))
    .map((schedule) => ({
      ...schedule,
      title: schedule.course?.title || "Untitled Course",
      type: schedule.mode === "Virtual" ? "Virtual Class" : "In Person Class",
    }));

  // 🔹 Format date range display
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const isSameDay = 
      start.getDate() === end.getDate() &&
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
    
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    
    if (isSameDay) {
      const dayName = dayNames[start.getDay()];
      const month = monthNames[start.getMonth()];
      const date = String(start.getDate()).padStart(2, "0");
      const year = start.getFullYear();
      return `${dayName}, ${month} ${date} ${year}`;
    } else {
      return `${start.toDateString()} - ${end.toDateString()}`;
    }
  };

  // 🔹 Format time for display
  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // 🔹 Get students for schedule
  const getStudentsForSchedule = (scheduleId) => {
    return (dashboard.students_details || []).filter(
      (student) => student.schedule_id === scheduleId
    );
  };

  return (
    <div className="w-full flex flex-col gap-6 p-6 bg-gray-50">

      {/* TOP BAR */}
      <div className="w-full flex items-center justify-between">
        <h2 className="text-zinc-800 text-2xl font-bold">Program Schedule</h2>

        <button 
          className="px-4 py-2 flex bg-[#4AACC5] hover:bg-[#75cee4] text-white text-sm font-semibold rounded-lg items-center gap-2 shadow-sm"
          onClick={handleclick}
        >
          <img src={PlusIcon} alt="" className="w-4 h-4" />
          Create Schedule
        </button>
      </div>

      {/* CALENDAR */}
      <div className="w-full bg-white rounded-lg border shadow-sm">
        <div className="flex items-center justify-between px-12 py-6 border-b">
          {monthsData.map((month) => (
            <span
              key={month.index}
              onClick={() => handleMonthChange(month.index)}
              className={`text-sm cursor-pointer ${
                month.index === selectedMonth
                  ? "text-zinc-800 font-medium text-base"
                  : "text-stone-300"
              }`}
            >
              {month.name}
            </span>
          ))}
        </div>

        <div className="overflow-x-auto px-12 py-4 ">
          <div className="flex gap-4 w-max">
            {dates.map((d, i) => (
              <div
                key={i}
                className="flex flex-col items-center cursor-pointer"
                onClick={() =>
                  setSelectedDate(
                    new Date(selectedDate.getFullYear(), selectedMonth, d.date)
                  )
                }
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    d.active ? "bg-[#4AACC5]" : "bg-indigo-50"
                  }`}
                >
                  <span className={d.active ? "text-white" : "text-neutral-500"}>
                    {d.date}
                  </span>
                </div>
                
                <span
                  className={`text-sm ${
                    d.active ? "text-zinc-800 font-medium" : "text-stone-300"
                  }`}
                >
                  {d.day}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex px-12 pb-6 text-zinc-800 text-xl font-medium">
          {formatSelectedDate()}
        </div>
      </div>

      {/* SCHEDULE */}
      <div className="w-full bg-white rounded-lg border shadow-sm p-8">
        <div className="overflow-y-auto max-h-[600px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="flex">

            {/* TIME COLUMN */}
            <div className="w-20 flex flex-col gap-10 pr-4 sticky left-0 bg-white z-10">
              {timeSlots.map((t, i) => (
                <div key={i} className="text-zinc-800 text-base font-normal">
                  {t}
                </div>
              ))}
            </div>

            {/* EVENTS COLUMN */}
            <div className="flex-1 relative" style={{ minHeight: '1000px' }}>
              {/* Grid lines */}
              <div className="absolute inset-0 flex flex-col">
                {timeSlots.map((_, i) => (
                  <div key={i} className="w-full h-10 border-t border-zinc-200" />
                ))}
              </div>

              {scheduleData.length > 0 ? (
                scheduleData.map((item, index) => {
                  const { top, height } = calculateEventPosition(item);
                  const scheduleStudents = getStudentsForSchedule(item.schedule_id);
                  
                  return (
                    <div
                      key={`schedule-${item.schedule_id}-${index}`}
                      className="absolute left-0 right-8 rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
                      style={{ 
                        top: `${top}px`, 
                        height: `${height}px`,
                        minHeight: '120px',
                        backgroundColor: '#F6FAFD'
                      }}
                    >
                      <div className="flex justify-between items-start h-full">

                        <div className="flex flex-col h-full flex-1">
                          {/* Title */}
                          <h3 className="text-zinc-800 text-base font-semibold mb-3 text-left">
                            {item.title}
                          </h3>

                          {/* Date */}
                          <div className="flex items-center gap-2 mb-2">
                            <img src={CalendarIcon} className="w-3.5 h-3.5" alt="" />
                            <span className="text-gray-600 text-xs">
                              {formatDateRange(item.start_date, item.end_date)}
                            </span>
                          </div>

                          {/* Time */}
                          <div className="flex items-center gap-2 mb-2">
                            <img src={ClockIcon} className="w-3.5 h-3.5" alt="" />
                            <span className="text-gray-600 text-xs">
                              {formatTime(item.start_time)} to {formatTime(item.end_time)}
                            </span>
                          </div>

                          {/* Students and Location */}
                          <div className="flex items-center justify-between mt-auto">
                            {/* Students */}
                            <div className="flex items-center gap-2">
                              <div className="flex -space-x-2">
                                {scheduleStudents.slice(0, 5).map((student, i) => (
                                  <img
                                    key={i}
                                    src={student.avatar_url || "https://placehold.co/28x28"}
                                    className="w-6 h-6 rounded-full border-2 border-white"
                                    alt="Student"
                                  />
                                ))}
                              </div>

                              <span className="text-gray-600 text-xs">
                                {scheduleStudents.length} {scheduleStudents.length === 1 ? "Student" : "Students"}
                              </span>
                            </div>

                            {/* Location */}
                            {item.location && (
                              <div className="flex items-center gap-1">
                                <img src={LocationIcon} className="w-3.5 h-3.5" alt="" />
                                <span className="text-gray-600 text-xs">
                                  {item.location}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* View Details */}
                        <div className="ml-4">
                          <button 
                            className="text-[#4AACC5] text-xs font-medium hover:underline"
                            onClick={() => {
                              console.log("View details:", item.schedule_id);
                            }}
                          >
                            View Details
                          </button>
                        </div>

                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="absolute top-20 left-1/2 transform -translate-x-1/2 text-gray-400 text-base">
                  No programs scheduled on this date
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

    </div>
  );
};

export default ProgramSchedule;